// Tasks page rules. Words: TASKS_PAGE_WORDS.md. Mockup: MOCKUP_TASKS_APPROVED.html (approved 2026-10-06).
// Data names: TASK_COPY_WORDS.md (safetag_task_copy.py) and safetag_sops_copy.py.
//   tasks/{taskId}                     the task
//   tasks/{taskId}/statusChanges/{id}  each On / Off flip: changedOn, changedBy, isActive, reason
// Pure: no database. Shared by the page, the server steps (functions/src/tasks.ts) and the tests.
//
// Rules:
// * Task ID and name are locked. JSP records and the SOP table carry them.
// * Off is never a delete. Past JSPs, SOP reads and QEW task records keep the task.
// * Every flip needs a one-line reason. Who, when and why are logged on the task.
// * Tier and Task Type change with a confirm and a reason. The tier sets the JSP rail;
//   the risk engine scores by Task Type.
// * Add Task (decision 35, CR-T2): no task without a tier and a Task Type. No default.
//   A new task starts Off. Task IDs are never reused, including the six left in the Sheet.

import { dateInSiteTime } from "./training-server";

export const TIERS = [1, 2, 3] as const;
export type Tier = (typeof TIERS)[number];

export const TASK_TYPES = ["Observe", "Operate-close", "Expose"] as const;
export type TaskType = (typeof TASK_TYPES)[number];

/** Left behind at the copy (TASK_COPY_WORDS.md). Their IDs are never handed out again. */
export const RETIRED_TASK_IDS = ["TASK-012", "TASK-022", "TASK-023", "TASK-024", "TASK-025"] as const;

/**
 * The task library (decision 68, TASK_SIZING_WORDS.md Section 3). Seeded Off by safetag_task_library_seed.py.
 * TASK-030 Disconnect Operation comes back on its approved SOP-28.25. TASK-031 Battery Charger Inspection
 * is added on its own. Reserved here so Add Task never hands one out. Next free: TASK-043.
 */
export const LIBRARY_TASK_IDS = ["TASK-030", ...Array.from({ length: 11 }, (_, i) => `TASK-${String(32 + i).padStart(3, "0")}`)] as const;

export const REASON_MAX = 200;
export const NAME_MAX = 60;

// ---- What Firestore holds ----

export interface Task {
  taskId: string;
  name: string;
  tier: number;
  taskType: string;
  isActive: boolean;
  sopNumber: string;
  likelihood1305C: string;
  authorizationPathway: string;
  lastDeactivatedOn: string;
  lastReactivatedOn: string;
}

/** One On / Off flip. The task copy and the SOP copy wrote the same shape. */
export interface StatusChange {
  changedOn: string;
  changedBy: string;
  changedByName?: string;
  isActive: boolean;
  reason: string;
  source?: string;
}

/** One Tier or Task Type change, kept in the task's editHistory. */
export interface FieldChange {
  action: string;
  field: "tier" | "taskType";
  from: string;
  to: string;
  reason: string;
  by: string;
  byName: string;
  at: string;
}

// ---- Words on the page ----

export const TIER_LINE: Record<Tier, string> = {
  1: "Observation",
  2: "Energized diagnostic",
  3: "ESWC required",
};

export function isTier(v: unknown): v is Tier {
  return typeof v === "number" && (TIERS as readonly number[]).includes(v);
}

export function isTaskType(v: unknown): v is TaskType {
  return typeof v === "string" && (TASK_TYPES as readonly string[]).includes(v);
}

/** "YES" / "NO" / "CONDITIONAL" as the Sheet wrote it, shown in plain case. */
export function likelyText(v: string): string {
  const s = v.trim().toUpperCase();
  if (s === "YES") return "Yes";
  if (s === "NO") return "No";
  if (s === "CONDITIONAL") return "Conditional";
  return v.trim();
}

/** "FIELD" shown as "Field". */
export function pathwayText(v: string): string {
  const s = v.trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";
}

// ---- Order and counts ----

function idNumber(taskId: string): number {
  const m = /^TASK-(\d+)$/.exec(taskId);
  return m?.[1] ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

/**
 * The order the Task Requirement Matrix copy listed them in: by Task Type
 * (Observe, Operate-close, Expose), then by ID. Within a tier that matches the mockup.
 */
function typeRank(t: string): number {
  const k = (TASK_TYPES as readonly string[]).indexOf(t);
  return k < 0 ? TASK_TYPES.length : k;
}

export interface TierGroup {
  tier: Tier;
  on: number;
  off: number;
  tasks: Task[];
}

/** Matches a search on name or ID, any case. */
export function taskMatches(t: Task, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return t.name.toLowerCase().includes(q) || t.taskId.toLowerCase().includes(q);
}

/** Tier 1, 2, 3. Inside a tier, on tasks first, then off. Counts are for the whole tier, not the search. */
export function groupTasks(tasks: Task[], search = ""): TierGroup[] {
  return TIERS.map((tier) => {
    const all = tasks.filter((t) => t.tier === tier);
    const shown = all
      .filter((t) => taskMatches(t, search))
      .sort((a, b) => Number(b.isActive) - Number(a.isActive) || typeRank(a.taskType) - typeRank(b.taskType) || idNumber(a.taskId) - idNumber(b.taskId));
    return { tier, on: all.filter((t) => t.isActive).length, off: all.filter((t) => !t.isActive).length, tasks: shown };
  });
}

/** A task whose tier is not 1, 2 or 3 (should not happen; the copy refuses one). */
export function strayTasks(tasks: Task[]): Task[] {
  return tasks.filter((t) => !isTier(t.tier));
}

// ---- IDs ----

/** The next free ID: one past every task on file, every retired ID and the task library. TASK-043 today. */
export function nextTaskId(existing: string[]): string {
  let max = 0;
  for (const id of [...existing, ...RETIRED_TASK_IDS, ...LIBRARY_TASK_IDS]) {
    const n = idNumber(id);
    if (n !== Number.MAX_SAFE_INTEGER && n > max) max = n;
  }
  return `TASK-${String(max + 1).padStart(3, "0")}`;
}

// ---- Checks ----

export function reasonProblem(reason: string): string | null {
  const r = reason.trim();
  if (!r) return "Type the reason.";
  if (r.length > REASON_MAX) return `Keep the reason under ${REASON_MAX} characters.`;
  return null;
}

export function sopNumberProblem(sop: string): string | null {
  const s = sop.trim();
  if (!s) return null;
  if (!/^SOP-\d+(\.\d+[a-z]?)?$/.test(s)) return "SOP number looks like SOP-28.47.";
  return null;
}

export interface NewTaskInput {
  name: string;
  tier: number | null;
  taskType: string;
  sopNumber: string;
}

/** Name, tier and Task Type are required. Tier and Task Type have no default. */
export function newTaskProblem(input: NewTaskInput, all: Task[]): string | null {
  const name = input.name.trim();
  if (!name) return "Enter the task name.";
  if (name.length > NAME_MAX) return `Keep the name under ${NAME_MAX} characters.`;
  const dupe = all.find((t) => t.name.trim().toLowerCase() === name.toLowerCase());
  if (dupe) return `${dupe.name} is already ${dupe.taskId}.`;
  if (!isTier(input.tier)) return "Pick the tier.";
  if (!isTaskType(input.taskType)) return "Pick the Task Type.";
  return sopNumberProblem(input.sopNumber);
}

/** Save turns on only when name, tier and Task Type are all there. */
export function canSaveNewTask(input: NewTaskInput): boolean {
  return Boolean(input.name.trim()) && isTier(input.tier) && isTaskType(input.taskType);
}

export function changeProblem(task: Task, field: "tier" | "taskType", to: unknown, reason: string): string | null {
  if (field === "tier") {
    if (!isTier(to)) return "Pick the tier.";
    if (to === task.tier) return `${task.name} is already Tier ${to}.`;
  } else {
    if (!isTaskType(to)) return "Pick the Task Type.";
    if (to === task.taskType) return `${task.name} is already ${to}.`;
  }
  return reasonProblem(reason);
}

// ---- Confirm wording ----

export function switchLine(task: Task, isOn: boolean, sopInLibrary: boolean): string {
  if (isOn) {
    const sop = task.sopNumber
      ? sopInLibrary
        ? ` It opens ${task.sopNumber}.`
        : ` ${task.sopNumber} is not in the SOP Library yet, so the SOP gate has no page to open.`
      : " It has no SOP number yet, so the SOP gate has no page to open.";
    return `It shows in the JSP task list at every site the next time a JSP starts.${sop}`;
  }
  return "It leaves the JSP task list. Nothing is deleted. Past JSPs, SOP reads and QEW task records keep it. While it is off, QEW task currency does not run down for it.";
}

export function changeLine(task: Task, field: "tier" | "taskType", to: string | number): string {
  if (field === "tier") {
    return `The JSP runs ${task.name} on the Tier ${to} rail on the next JSP, at every site. JSPs already filed keep the tier they were done under.`;
  }
  return `The risk engine scores ${task.name} as ${to} on the next JSP. JSPs already filed keep the type they were done under.`;
}

// ---- History ----

export interface HistoryLine {
  at: string;
  text: string;
  who: string;
  reason: string;
}

/** On / Off flips and Tier or Task Type changes, newest first. */
export function historyLines(changes: StatusChange[], edits: FieldChange[]): HistoryLine[] {
  const flips = changes.map((c) => ({
    at: c.changedOn,
    text: c.isActive ? "Turned on" : "Turned off",
    who: c.changedByName || c.changedBy,
    reason: c.reason,
  }));
  const fields = edits
    .filter((e) => e.field === "tier" || e.field === "taskType")
    .map((e) => ({
      at: e.at,
      text: e.field === "tier" ? `Tier ${e.from} to Tier ${e.to}` : `Task Type ${e.from} to ${e.to}`,
      who: e.byName || e.by,
      reason: e.reason,
    }));
  return [...flips, ...fields].sort((a, b) => b.at.localeCompare(a.at));
}

/** 2026-10-07T01:10:04Z -> 10/06/2026 (site time). A bare date stays that date. */
export function shortDay(iso: string): string {
  const day = /T/.test(iso) && !Number.isNaN(new Date(iso).getTime()) ? dateInSiteTime(new Date(iso)) : iso;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}
